/**
 * StudioFeatureBanner — a high-visibility entry-point card for the Control
 * Room. One shared component renders the AI Music Studio, AI Avatar Studio,
 * and AI News Studio banners (and any future studio we add) with a single
 * source of truth for the layout, animation, and workspace-genre tinting.
 *
 * Each banner shows:
 *   – workspace-genre tinted top accent + ambient gradient
 *   – icon + eyebrow + "NEW" badge
 *   – big bold title
 *   – descriptive copy
 *   – capability chips (5 max, each with its own icon)
 *   – a live counter on the right (`countEndpoint` is polled; the result
 *     either has a top-level `total` field or an array under `arrayKey`
 *     whose length is used as the counter)
 *   – an "Open <Label>" CTA tinted with the active station theme
 *
 * Props
 *   to            (string)  Internal route the card links to
 *   icon          (Lucide)  Icon shown in the workspace-tinted square
 *   eyebrow       (string)  Short eyebrow above the title (will be uppercased)
 *   title         (string)  Big bold headline
 *   description   (string)  Sub-headline copy
 *   capabilities  (array)   [{ icon, label }, …] — up to 5 rendered as chips
 *   countEndpoint (string)  GET endpoint relative to /api for the live count
 *   countParse    (fn?)     Optional (resp.data) → number override
 *   countLabel    (string)  "tracks", "videos", "stories", … (singular form)
 *   countPlural   (string)  Optional plural form (defaults to label + "s")
 *   ctaLabel      (string)  CTA button copy, e.g. "Open AI Music Studio"
 *   testid        (string)  Stable data-testid for the outer Link
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { getStationTheme } from "@/lib/stationTheme";
import { useT } from "@/lib/i18n";

export default function StudioFeatureBanner({
  to,
  icon: Icon,
  eyebrow,
  title,
  description,
  capabilities = [],
  countEndpoint,
  countParse,
  countLabel = "items",
  countPlural,
  ctaLabel = "Open studio",
  testid,
}) {
  const t = useT();
  const [theme, setTheme] = useState(getStationTheme(""));
  const [count, setCount] = useState(null);
  const [pulse, setPulse] = useState(false);
  const prevCountRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const s = await api.get("/stations/me");
      setTheme(getStationTheme(s.data?.genre || ""));
    } catch { /* anon / no station */ }
    if (countEndpoint) {
      try {
        const r = await api.get(countEndpoint);
        let n = null;
        if (typeof countParse === "function") {
          n = countParse(r.data);
        } else if (typeof r.data?.total === "number") {
          n = r.data.total;
        } else if (Array.isArray(r.data)) {
          n = r.data.length;
        } else if (Array.isArray(r.data?.items)) {
          n = r.data.items.length;
        } else if (Array.isArray(r.data?.videos)) {
          n = r.data.videos.length;
        }
        // If the count went up since the last fetch, pulse the number
        // briefly so the user notices new content arriving live.
        if (
          prevCountRef.current !== null &&
          typeof n === "number" &&
          n > prevCountRef.current
        ) {
          setPulse(true);
          window.setTimeout(() => setPulse(false), 1400);
        }
        prevCountRef.current = n;
        setCount(n);
      } catch { setCount(null); }
    }
  }, [countEndpoint, countParse]);

  useEffect(() => { refresh(); }, [refresh]);
  // Auto-refresh every 60s so newly-generated assets show up without a
  // page reload. Lightweight — each banner hits a small list endpoint.
  useEffect(() => {
    if (!countEndpoint) return undefined;
    const t = setInterval(() => { refresh(); }, 60000);
    return () => clearInterval(t);
  }, [refresh, countEndpoint]);
  useEffect(() => {
    const onSwitch = () => {
      // Treat station switches as a hard reset so a previous station's
      // count doesn't trigger a misleading pulse on the new one.
      prevCountRef.current = null;
      refresh();
    };
    window.addEventListener("station-switched", onSwitch);
    return () => window.removeEventListener("station-switched", onSwitch);
  }, [refresh]);

  const plural = countPlural || `${countLabel}s`;
  const countWord = count === 1 ? countLabel : plural;

  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block rounded-xl border bg-bg-surface overflow-hidden lift relative"
      style={theme.borderStyle}
    >
      {/* Genre-tinted accent stripe along the top */}
      <div className="h-1.5 w-full" style={theme.barStyle} />

      {/* Subtle ambient gradient */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background: `radial-gradient(circle at 20% 50%, ${theme.hex}33 0%, transparent 50%)`,
        }}
      />

      <div className="relative p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
        {/* Left side — identity & headline */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
              style={theme.tintStyle}
            >
              {Icon && <Icon className="h-5 w-5" style={{ color: theme.hex }} />}
            </div>
            <span
              className="font-mono text-[10px] uppercase tracking-[0.2em]"
              style={{ color: theme.hex }}
            >
              {eyebrow}
            </span>
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-black tracking-tighter text-ink-primary mb-1.5">
            {t(title)}
          </h3>
          <p className="text-sm text-ink-secondary max-w-2xl">{t(description)}</p>

          {/* Capabilities row */}
          {capabilities.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-ink-muted">
              {capabilities.slice(0, 5).map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1.5">
                  {c.icon && <c.icon className="h-3 w-3" style={{ color: theme.hex }} />}
                  {t(c.label)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right side — CTA + count */}
        <div className="flex items-center gap-4 sm:flex-shrink-0">
          {count !== null && (
            <div className="text-right hidden sm:block">
              <div
                className={`font-display text-3xl font-black tracking-tighter leading-none transition-transform ${
                  pulse ? "count-pulse" : ""
                }`}
                style={{ color: theme.hex }}
                data-testid={`${testid}-count`}
              >
                {count}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mt-1">
                {countWord} in library
              </div>
            </div>
          )}
          <span
            className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 font-bold text-sm text-bg whitespace-nowrap"
            style={{ backgroundColor: theme.hex, ...theme.glowStyle }}
          >
            {t(ctaLabel)}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}
